/* ======================================================================= */
/* Les coulisses d'un fil — contrat + derives d'affichage.                  */
/*                                                                          */
/* Ce module est PUR : aucun import applicatif, donc testable et rendable    */
/* hors de l'application. La lecture reseau vit dans `useCoulisses.ts`.      */
/*                                                                          */
/* Ce qu'on affiche, et d'ou ca vient (cote Cyrano,                          */
/* `app/domain/services/coulisses_du_fil.py`) :                              */
/*                                                                          */
/*   l'OFFERT      le compteur unique du fan sur la fenetre courante         */
/*   le FLIRTY     ce que chaque message a coute a son enveloppe de sexting  */
/*   les PARCOURS  ce qu'un message a OUVERT, et quand ca s'est referme      */
/*                                                                          */
/* /!\ AUCUN EURO NE BOUGE. Le ledger de ce compteur est en LECTURE SEULE :  */
/* il re-derive une somme a chaque appel, il n'ecrit rien. C'est l'enveloppe */
/* de sexting GRATUIT que la machine s'autorise a offrir avant de basculer   */
/* en vente — un budget de generosite, pas une facture. Le 13-08 l'ecran     */
/* disait « SOLDE », et ca s'est lu comme un compte debite au fan.           */
/*                                                                          */
/* /!\ DEUX FENETRES, et c'est voulu : l'offert du haut parle de la fenetre  */
/* courante (12 h) ; les pastilles disent ce que chaque message a coute      */
/* QUAND IL EST PARTI — donc dans sa fenetre a lui, close depuis longtemps   */
/* sur un message d'avant-hier. Les deux sont vrais, ils ne repondent pas a  */
/* la meme question. L'ecran ne doit jamais les additionner.                 */
/* ======================================================================= */

/** Le vocabulaire FERME du ledger. Sept motifs : un qui coute, six qui ne
 *  coutent rien pour six raisons DIFFERENTES.
 *
 *  Le type est ouvert (`| string`) exprès : le backend peut en publier un
 *  huitieme avant que le cockpit ne le connaisse, et un motif inconnu doit
 *  s'AFFICHER tel quel — c'est une information, pas une erreur de rendu. */
export type MotifCoulisse =
  | "facture"
  | "entrant"
  | "tape_a_la_main"
  | "media"
  | "prix"
  | "hors_tour_ordinaire"
  | "hors_sequence"
  | "hors_fenetre"
  | (string & {});

export interface CoulisseMessage {
  message_id: string;
  motif: MotifCoulisse;
  flirty: boolean;
  cout_cents: number;
  geste?: string | null;
  parcours_du_geste?: string | null;
}

/** `ouvert` | `termine` | `abandonne`. */
export type MomentParcours = "ouvert" | "termine" | "abandonne" | (string & {});

export interface CoulisseMoment {
  quoi: MomentParcours;
  at: string;
  /** `null` est EXACT et frequent : un parcours ouvert par un seuil de budget
   *  ou par un paiement ne nait sur AUCUN message du fan. C'est
   *  `declencheur_sur` qui l'explique. */
  message_id?: string | null;
  detail?: string;
}

export interface CoulisseCreneau {
  creneau: string;
  etat: string;
  refuse?: boolean;
  valeur?: string | null;
  extrait?: string | null;
  source_message_id?: string | null;
  capte_le?: string | null;
}

export interface CoulisseParcours {
  nom: string;
  quand?: string;
  type?: string;
  statut: string;
  entrants?: number;
  a_demander?: string | null;
  ouvert_le: string;
  ferme_le?: string | null;
  declencheur?: string;
  declencheur_libelle?: string;
  declencheur_sur?: string;
  moments?: CoulisseMoment[];
  creneaux?: CoulisseCreneau[];
}

/** Le compteur unique, tel que `EtatEconomie.to_state()` le serialise.
 *  Tous les champs sont optionnels : le cockpit part en ligne AVANT Cyrano. */
export interface CoulisseSolde {
  credits_restants_cents?: number;
  credits_plafond_cents?: number;
  credits_depense_cents?: number;
  credits_medias_cents?: number;
  credits_sexting_cents?: number;
  medias_envoyes?: number;
  medias_plafond?: number;
  fenetre_heures?: number;
  statut?: string;
  pourcentage?: number;
  bareme_cents?: number;
  pente_pourcent?: number;
  palier?: number;
  palier_nom?: string | null;
  sexting_continue?: boolean;
}

export interface CoulissePoint {
  at: string;
  depense_cents: number;
}

export interface CoulisseActivite {
  at: string;
  entrants: number;
  sortants: number;
}

/** `menu` · `devis` · `lien` · `paye` · `cadeau` · `surprise` · `refus`. */
export interface CoulisseJalon {
  at: string;
  quoi: string;
  montant_cents: number;
}

/** `ultimatum` · `relance_du_jeu` · `demande_de_partie` · les trois de la
 *  MAIN rendue (`annonce_payante` · `question_de_prix` · `accusation_bot`).
 *
 *  Ouvert (`| string`) expres, comme `MotifCoulisse` : Cyrano peut en publier
 *  un de plus avant que le cockpit ne le connaisse, et un moment inconnu
 *  doit s'AFFICHER sous sa cle plutot que de disparaitre. */
export type QuoiDuMoment =
  | "ultimatum"
  | "relance_du_jeu"
  | "demande_de_partie"
  | "annonce_payante"
  | "question_de_prix"
  | "accusation_bot"
  | (string & {});

/** Un moment du fil qui n'appartient a AUCUN parcours — et qui coupe.
 *
 *  🩸 Cyrano 2.0 n'ouvre aucun dossier : il joue un geste et referme le tour.
 *  Les bandes de parcours (`BandeDuFil`, plus bas) se derivent de
 *  `phase_dossier` et ne peuvent donc RIEN dire de lui. Ceux-la se derivent du
 *  geste stampe sur la bulle, et du texte pour la demande de partie. */
export interface MomentDuTour {
  quoi: QuoiDuMoment;
  at: string;
  /** LE message qui porte ce moment. Jamais absent — a la difference d'un
   *  `CoulisseMoment`, ces trois-la naissent tous d'un message. */
  message_id: string;
  /** Cyrano s'est-il tu APRES ce message ?
   *
   *  ⚠️ Ce n'est PAS l'etat courant de la conversation (le bandeau du haut le
   *  sert deja) : c'est ce que CE moment-la a fait. Les deux divergent des que
   *  Kayla a repris la main entre-temps. */
  pause?: boolean;
  /** 🎰 `demande_de_partie` seulement : le pack que le bouton du jeu a nommé
   *  (« Je veux rejouer : 3 parties (50 €) »). Absent sur l'ancien bouton, et
   *  sur un Cyrano qui ne le sert pas encore (le cockpit part avant). */
  parties?: number | null;
  prix_eur?: number | null;
}

/** 🎰 Le pack de la DERNIÈRE demande de partie du fil — le conseil du
 *  chatteur (Kayla, 25-09-2026). `null` si le fan n'a rien demandé, ou par
 *  l'ancien bouton qui ne nommait rien. */
export function derniereDemandeDePack(
  moments: MomentDuTour[] | undefined,
): { parties: number; prixEur: number | null } | null {
  let dernier: MomentDuTour | null = null;
  for (const m of moments ?? []) {
    if (m.quoi !== "demande_de_partie" || m.parties == null) continue;
    if (!dernier || m.at > dernier.at) dernier = m;
  }
  if (!dernier || dernier.parties == null) return null;
  return { parties: dernier.parties, prixEur: dernier.prix_eur ?? null };
}

export interface CoulissePalier {
  pourcentage: number;
  nom: string;
  sexting_continue?: boolean;
}

export interface Coulisses {
  /** `null` = « on ne compte pas » (compteur eteint ou illisible).
   *  /!\ Ne JAMAIS l'afficher comme un solde a zero : le premier dit qu'on
   *  n'a pas compte, le second qu'un fan a tout depense. */
  solde: CoulisseSolde | null;
  prix_du_message_cents: number;
  fenetre_heures: number;
  messages: CoulisseMessage[];
  parcours: CoulisseParcours[];
  /** Ce qui a COUPE Cyrano dans ce fil. Optionnel : le cockpit part en ligne
   *  AVANT Cyrano (~3 min), et pendant cette fenetre la route repond sans ce
   *  champ. Toujours lu via `?? []`. */
  moments?: MomentDuTour[];
  /** Le plus vieux message annote. Au-dela, l'ecran ne sait rien — et il doit
   *  le dire plutot que d'afficher « rien ne s'est passe ». */
  depuis: string | null;

  /** La CONSOMMATION de la fenetre glissante, en marches d'escalier. Ce n'est
   *  PAS le solde : le plafond d'une fenetre passee ne se rejoue pas (le bareme
   *  suit le statut, la pente le retranche). Le graphe trace `plafond_cents` et
   *  les `paliers` en lignes par-dessus — une ligne qu'on sait actuelle ne ment
   *  sur rien. */
  courbe?: CoulissePoint[];
  activite?: CoulisseActivite[];
  jalons?: CoulisseJalon[];
  paliers?: CoulissePalier[];
  /** `0` = on ne compte pas : ni ligne de plafond, ni paliers. */
  plafond_cents?: number;
}

/**
 * Vide, et de la bonne FORME.
 *
 * 🩸 Vit ICI, dans le module pur, et pas dans `useCoulisses.ts` : c'est la
 * seule façon qu'un producteur de coulisses autre que la route (le mode démo)
 * puisse s'en servir sans importer le client HTTP — donc sans cycle. Le
 * 19-08-2026, la démo a rendu `{ messages: [], tours: [] }` à la place : le
 * champ `parcours` manquait, `ParcoursEnCours` a fait `.filter` sur
 * `undefined`, et TOUT le panneau fan est tombé dans son ErrorBoundary. Une
 * forme qui se devine est une forme qui se devine faux.
 */
export const COULISSES_VIDES: Coulisses = {
  solde: null,
  prix_du_message_cents: 0,
  fenetre_heures: 0,
  messages: [],
  parcours: [],
  moments: [],
  depuis: null,
  courbe: [],
  activite: [],
  jalons: [],
  paliers: [],
  plafond_cents: 0,
};

/**
 * Les coulisses PRIVEES des parcours refermes dont le workflow n'existe plus.
 *
 * 🩸 Un dossier de parcours vit en base et rien ne le referme quand son
 * workflow quitte le disque. Cyrano 2.0 a ramene huit parcours a deux le
 * 16-08-2026 ; mesure le 19-08, **80 conversations** portaient encore dans
 * leur fil un `vente_niveau_1`, `montrer_l_offre`, `surprise`, `qualification`
 * ou `apres_vente` — 130 dossiers, dont 121 abandonnes. Ils se nommaient a
 * quatre endroits a la fois (la pastille « En cours », l'aplat « DECLENCHE »,
 * la bande du fil, le rail), parce que les quatre derivent de `parcours`. On
 * coupe donc ICI, a la source.
 *
 * ⛔ **Un parcours OUVERT n'est jamais cache, meme hors catalogue.** Un dossier
 * qui TOURNE sur un workflow qu'on ne sert plus est exactement celui qu'il faut
 * regarder — le masquer le rendrait introuvable. On ne retire que du CLOS :
 * `complet` et `abandon` racontent une machine qui n'existe plus, et il n'y a
 * rien a en faire. (Zero cas ouvert en prod au 19-08 : les 8 dossiers ouverts
 * sont tous des `ultimatum`.)
 *
 * 🪤 `servis` vide ou absent = ON NE FILTRE RIEN. Le catalogue vient d'un appel
 * reseau (`/api/phases`) qui rend 404 pendant la fenetre de deploiement — le
 * cockpit part en ligne avant Cyrano. Filtrer sur une reponse manquante
 * effacerait TOUT le fil, y compris ce qui tourne. Dans le doute, on montre.
 *
 * La reference d'entree est rendue TELLE QUELLE quand il n'y a rien a jeter —
 * les memos de `views.tsx` (bandes, segments, creneaux) en dependent, et un
 * objet neuf a chaque render re-rendrait toute la liste de bulles.
 */
export function parcoursAuCatalogue(
  coulisses: Coulisses,
  servis: readonly string[] | null | undefined,
): Coulisses {
  if (!servis || servis.length === 0) return coulisses;
  const connus = new Set(servis);
  const gardes = coulisses.parcours.filter(
    (p) => p.statut === "ouvert" || connus.has(p.nom),
  );
  if (gardes.length === coulisses.parcours.length) return coulisses;
  return { ...coulisses, parcours: gardes };
}

/** Un moment de parcours, aplati pour etre pose dans le fil. */
export interface BandeDuFil {
  cle: string;
  ts: number;
  quoi: MomentParcours;
  parcours: string;
  /** Le nom sous lequel Kayla cherche le declencheur, pas la cle technique. */
  declencheur: string;
  /** `le_texte` | `l_etat` | `evenement` — ce qui EXPLIQUE un message absent. */
  sur: string;
  detail: string;
  messageId: string | null;
  /** La MEME teinte que le rail du segment. Recalculer `teinteDuParcours` chez
   *  le composant redonnerait la couleur d'AVANT resolution de collision, et
   *  la bande ne peindrait pas comme son propre trait. */
  couleur: string;
}

/** Les moments de tous les parcours, dans l'ordre du temps.
 *
 *  Un parcours sans aucun moment journalise rend quand meme sa bande
 *  d'ouverture, batie sur `ouvert_le` : le journal des parcours date du 12-08,
 *  et un dossier plus vieux que lui existe sans avoir laisse d'event. Preferer
 *  une bande sans `message_id` a pas de bande du tout — l'ouverture a bien eu
 *  lieu, c'est le POINTEUR qui manque, et l'ecran le dit. */
export function bandesDuFil(parcours: CoulisseParcours[]): BandeDuFil[] {
  const teintes = teintesDuFil(parcours);
  const out: BandeDuFil[] = [];
  for (const p of parcours) {
    const declencheur = p.declencheur_libelle || p.declencheur || "";
    const sur = p.declencheur_sur || "";
    const couleur = teintes.get(p.nom) ?? teinteDuParcours(p.nom);
    const moments = p.moments ?? [];
    if (moments.length === 0) {
      out.push({
        cle: `parcours-${p.nom}-ouvert-${p.ouvert_le}`,
        ts: Date.parse(p.ouvert_le),
        quoi: "ouvert",
        parcours: p.nom,
        declencheur,
        sur,
        detail: "",
        messageId: null,
        couleur,
      });
      if (p.ferme_le) {
        out.push({
          cle: `parcours-${p.nom}-fin-${p.ferme_le}`,
          ts: Date.parse(p.ferme_le),
          quoi: p.statut === "abandon" ? "abandonne" : "termine",
          parcours: p.nom,
          declencheur,
          sur,
          detail: "",
          messageId: null,
          couleur,
        });
      }
      continue;
    }
    for (const m of moments) {
      out.push({
        cle: `parcours-${p.nom}-${m.quoi}-${m.at}`,
        ts: Date.parse(m.at),
        quoi: m.quoi,
        parcours: p.nom,
        declencheur,
        sur,
        detail: m.detail ?? "",
        messageId: m.message_id ?? null,
        couleur,
      });
    }
  }
  return out.sort((a, b) => a.ts - b.ts);
}

/** Ce qu'un message a DECLENCHE, indexe par id de message.
 *
 *  Un meme message peut ouvrir DEUX parcours — mesure cote Cyrano le 12-08 :
 *  « Interessant ton menu .. tu fais du reel ? » ouvre `montrer_l_offre` ET
 *  `qualification`. La valeur est donc une liste, jamais un scalaire. */
export function declenchementsParMessage(
  bandes: BandeDuFil[],
): Map<string, BandeDuFil[]> {
  const table = new Map<string, BandeDuFil[]>();
  for (const b of bandes) {
    if (!b.messageId) continue;
    const deja = table.get(b.messageId);
    if (deja) deja.push(b);
    else table.set(b.messageId, [b]);
  }
  return table;
}

/** Les moments du tour, indexes par LE message qui les porte.
 *
 *  Une liste par message, pas un scalaire — pour la meme raison que
 *  `declenchementsParMessage` : rien n'interdit qu'une meme bulle porte deux
 *  moments le jour ou un quatrieme s'ajoute. */
export function momentsParMessage(
  moments: MomentDuTour[] | undefined,
): Map<string, MomentDuTour[]> {
  const table = new Map<string, MomentDuTour[]>();
  for (const m of moments ?? []) {
    if (!m.message_id) continue;
    const deja = table.get(m.message_id);
    if (deja) deja.push(m);
    else table.set(m.message_id, [m]);
  }
  return table;
}

/** Le mot que Kayla lit, dans les termes ou elle a demande l'ecran (19-08).
 *
 *  /!\ Un `quoi` inconnu rend sa CLE brute plutot qu'un mot invente — meme
 *  regle que `libelleDuMotif` : le jour ou Cyrano en publie un quatrieme,
 *  l'ecran le montre au lieu de le taire. */
export function libelleDuMoment(quoi: QuoiDuMoment): string {
  switch (quoi) {
    case "ultimatum":
      return "Ultimatum";
    case "relance_du_jeu":
      // « cet ultimatum, c'est un ultimatum different parce que c'est celui
      // qui va lui conseiller de rejouer une partie » (Kayla, 18-08). Elle
      // l'appelle « l'ultimatum du jeu » — c'est ce nom-la qui s'affiche, pas
      // la cle technique du geste (`rejouer_une_partie`).
      return "Ultimatum du jeu";
    case "demande_de_partie":
      return "Demande d'une nouvelle partie";
    // Les trois moments ou Cyrano REPOND puis rend la main (Kayla, 01-09).
    // Les libelles disent ce qui s'est passe, pas la cle technique : c'est un
    // chatteur qui les lit en ouvrant la conversation qui clignote.
    case "annonce_payante":
      return "Il a dit que c'est payant";
    case "question_de_prix":
      return "Il demande les prix";
    // /!\ Le libelle est plus LARGE que sa cle, et c'est voulu. Kayla a range
    // DEUX familles sous ce motif le 01-09 : celui qui a crame le bot (« t'es
    // une IA »), et celui qui crie a l'arnaque (« t'es fake », « tu m'as
    // arnaque »). Renommer la cle aurait demande de re-livrer le backend et le
    // cockpit dans le bon ordre pour un renommage ; le libelle, lui, se change
    // seul et sans risque. « Il a crame le bot » aurait menti une fois sur
    // deux, et c'est le mot que le chatteur lit.
    case "accusation_bot":
      return "Il ne te croit plus";
    default:
      return quoi;
  }
}

/** Les creneaux remplis, indexes par le message qui les a remplis. */
export function creneauxParMessage(
  parcours: CoulisseParcours[],
): Map<string, { parcours: string; creneau: CoulisseCreneau }[]> {
  const table = new Map<string, { parcours: string; creneau: CoulisseCreneau }[]>();
  for (const p of parcours) {
    for (const c of p.creneaux ?? []) {
      if (!c.source_message_id) continue;
      const entree = { parcours: p.nom, creneau: c };
      const deja = table.get(c.source_message_id);
      if (deja) deja.push(entree);
      else table.set(c.source_message_id, [entree]);
    }
  }
  return table;
}

/* ---------------------------------------------------------------------- */
/* La couleur d'un workflow                                                */
/* ---------------------------------------------------------------------- */

/** Les six teintes des workflows, DANS CET ORDRE.
 *
 *  Validees par `dataviz/scripts/validate_palette.js` sur les deux surfaces du
 *  cockpit (#090909 sombre, #ffffff clair) : bande de clarte, plancher de
 *  chroma, separation daltonienne des paires adjacentes, plancher vision
 *  normale, contraste — 6 PASS sur 6, dans les deux modes.
 *
 *  ⛔ Ni rose, ni ambre, ni emeraude : ce sont les couleurs d'ETAT du cockpit
 *  (succes / attention / alerte, cf. la jauge de solde et la frise). Les
 *  reutiliser comme identite de workflow ferait lire « ce parcours va mal » la
 *  ou il n'y a qu'un nom.
 *
 *  ⛔ Et l'ordre ne se remelange pas : une couleur suit l'ENTITE, jamais son
 *  rang. Deux fans dont les parcours ouverts different doivent voir
 *  `qualification` de la meme couleur — sinon la couleur n'apprend rien. */
export const TEINTES_PARCOURS = [
  "#0284c7",
  "#be185d",
  "#65a30d",
  "#8b5cf6",
  "#ea580c",
  "#0d9488",
] as const;

/** La teinte d'un workflow, DERIVEE DE SON NOM.
 *
 *  Un hash stable plutot qu'un index dans la liste du fan : le second
 *  repeindrait `qualification` d'une conversation a l'autre selon ce qui s'y
 *  est ouvert d'autre. Deux noms peuvent tomber sur la meme teinte — c'est
 *  sans consequence, chaque rail porte son NOM ecrit a cote, donc l'identite
 *  ne repose jamais sur la couleur seule. */
export function teinteDuParcours(nom: string): string {
  let h = 0;
  for (let i = 0; i < nom.length; i++) h = (h * 31 + nom.charCodeAt(i)) | 0;
  return TEINTES_PARCOURS[Math.abs(h) % TEINTES_PARCOURS.length];
}

/** Un parcours vu comme un SEGMENT de temps : de son ouverture a sa fin. */
export interface SegmentDuFil {
  nom: string;
  couleur: string;
  debut: number;
  /** `null` = il court toujours. */
  fin: number | null;
  statut: string;
}

/** La teinte de CHAQUE parcours de ce fil, collisions resolues.
 *
 *  🪤 **Six teintes, onze workflows sur le disque : les collisions sont
 *  CERTAINES, pas improbables.** Mesure sur le harnais : `surprise` et
 *  `qualification` tombaient sur la meme teinte, et deux rails identiques dans
 *  un meme fil ne distinguent plus rien — c'est exactement ce que la couleur
 *  est la pour faire.
 *
 *  On garde donc la teinte derivee du NOM (stable d'une conversation a
 *  l'autre) et on ne resout la collision que DANS ce fil : parcours tries par
 *  nom, chacun prend sa teinte si elle est libre, la suivante sinon. Deux fils
 *  differents peuvent donc peindre le meme workflow de deux teintes quand il y
 *  a collision — le prix est admis, parce que chaque rail porte son NOM ecrit
 *  a cote et que l'identite n'a jamais repose sur la couleur seule. */
export function teintesDuFil(parcours: { nom: string }[]): Map<string, string> {
  const pris = new Set<string>();
  const teintes = new Map<string, string>();
  for (const nom of [...new Set(parcours.map((p) => p.nom))].sort()) {
    let teinte = teinteDuParcours(nom);
    if (pris.has(teinte) && pris.size < TEINTES_PARCOURS.length) {
      const depart = TEINTES_PARCOURS.indexOf(teinte as (typeof TEINTES_PARCOURS)[number]);
      for (let k = 1; k <= TEINTES_PARCOURS.length; k++) {
        const suivante = TEINTES_PARCOURS[(depart + k) % TEINTES_PARCOURS.length];
        if (!pris.has(suivante)) {
          teinte = suivante;
          break;
        }
      }
    }
    pris.add(teinte);
    teintes.set(nom, teinte);
  }
  return teintes;
}

export function segmentsDuFil(parcours: CoulisseParcours[]): SegmentDuFil[] {
  const teintes = teintesDuFil(parcours);
  return parcours
    .map((p) => ({
      nom: p.nom,
      couleur: teintes.get(p.nom) ?? teinteDuParcours(p.nom),
      debut: Date.parse(p.ouvert_le),
      fin: p.ferme_le ? Date.parse(p.ferme_le) : null,
      statut: p.statut,
    }))
    .filter((s) => Number.isFinite(s.debut))
    .sort((a, b) => a.debut - b.debut);
}

/** Ce message tombe-t-il dans un parcours ? Rend les segments actifs a cet
 *  instant — plusieurs, parce que deux parcours peuvent courir ensemble. */
export function segmentsActifs(ts: number, segments: SegmentDuFil[]): SegmentDuFil[] {
  return segments.filter((s) => ts >= s.debut && (s.fin === null || ts <= s.fin));
}

/** Un montant en centimes, en euros lisibles. `-` prefixe les debits. */
export function euros(cents: number, { signe = false }: { signe?: boolean } = {}): string {
  const valeur = Math.abs(cents) / 100;
  const texte = valeur.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${signe && cents !== 0 ? "−" : ""}${texte} €`;
}

/** Le libelle d'un motif — ce que le message a fait, dans les mots de Kayla.
 *
 *  /!\ Un motif inconnu rend sa CLE brute plutot qu'un mot invente : le jour
 *  ou Cyrano en publie un huitieme, l'ecran le montre au lieu de le taire. */
export function libelleDuMotif(motif: MotifCoulisse): string {
  switch (motif) {
    case "facture":
      return "compté";
    case "entrant":
      return "c'est lui";
    case "tape_a_la_main":
      // Le huitieme motif, arrive cote Cyrano le 13-08 (« c'est pas une
      // discussion de Cyrano, donc ca devrait jamais etre facture »). Il
      // s'affichait deja — brut — parce qu'un motif inconnu se montre tel
      // quel ; il a maintenant ses mots.
      return "à la main";
    case "media":
      return "média";
    case "prix":
      return "un prix";
    case "hors_tour_ordinaire":
      return "pas un tour ordinaire";
    case "hors_fenetre":
      return "fenêtre passée";
    case "hors_sequence":
      return "";
    default:
      return motif;
  }
}

/** Part consommee de l'enveloppe, bornee [0, 100]. `null` quand on ne sait
 *  pas — un plafond a zero n'est PAS une enveloppe pleine. */
export function partConsommee(solde: CoulisseSolde | null): number | null {
  if (!solde) return null;
  const plafond = solde.credits_plafond_cents ?? 0;
  if (plafond <= 0) return null;
  const depense = solde.credits_depense_cents ?? 0;
  return Math.max(0, Math.min(100, Math.round((depense / plafond) * 100)));
}
